import dayjs from "dayjs"
import { useEffect } from "react"
import { atom, useRecoilState } from "recoil"
import {
  ClientFragment,
  Enum_Feature_Type_Enum,
  Enum_Trend_Direction_Enum,
} from "../generated/graphql"
import { colors } from "../utils/theme"
import {
  Activity,
  ChartDataPoints,
  Language,
  Sleep,
  Trends,
} from "../utils/types"

const trendsState = atom<Trends | undefined>({
  key: "trendsState",
  default: undefined,
})

const activityState = atom<Activity | undefined>({
  key: "activitiesState",
  default: undefined,
})

const categoriesState = atom<string[] | undefined>({
  key: "categoriesState",
  default: undefined,
})

const sleepState = atom<Sleep[] | undefined>({
  key: "sleepState",
  default: undefined,
})

const morningnessState = atom<ChartDataPoints[][] | undefined>({
  key: "morningnessState",
  default: undefined,
})

const languageState = atom<Language | undefined>({
  key: "languageState",
  default: undefined,
})

const socialMediaState = atom<ChartDataPoints[][] | undefined>({
  key: "socialMediaState",
  default: undefined,
})

const timeAtHomeState = atom<ChartDataPoints[][] | undefined>({
  key: "timeAtHomeState",
  default: undefined,
})

const initialTrends: Trends = {
  walk: {
    label: "Walk",
    value: "",
    unit: "min",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  exercise: {
    label: "Exercise",
    value: "",
    unit: "min",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  stationary: {
    label: "Stationary",
    value: "",
    unit: "min",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  driving: {
    label: "In Transit",
    value: "",
    unit: "min",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  bedtime: {
    label: "Bedtime",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  waketime: {
    label: "Waketime",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  timeInBed: {
    label: "Time in Bed",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  morningness: {
    label: "Morningness",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  positiveWords: {
    label: "Positive Words",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  negativeWords: {
    label: "Negative Words",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  selfFocusWords: {
    label: "Self Focus Words",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  rigidWords: {
    label: "Rigid Words",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  activeSocialMedia: {
    label: "Words Typed",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
  timeAtHome: {
    label: "Time at Home",
    value: "",
    direction: Enum_Trend_Direction_Enum.NoChange,
  },
}

const missingDay = (currentDate: dayjs.Dayjs, previousDate: dayjs.Dayjs) =>
  currentDate.format("YYYY-MM-DD") !==
  previousDate.add(1, "day").format("YYYY-MM-DD")

export const useTrends = (client: ClientFragment) => {
  const [trends, setTrends] = useRecoilState<Trends | undefined>(trendsState)
  const [activity, setActivity] = useRecoilState<Activity | undefined>(
    activityState
  )
  const [categories, setCategories] = useRecoilState<string[] | undefined>(
    categoriesState
  )
  const [sleep, setSleep] = useRecoilState<Sleep[] | undefined>(sleepState)
  const [morningness, setMorningness] = useRecoilState<
    ChartDataPoints[][] | undefined
  >(morningnessState)
  const [language, setLanguage] = useRecoilState<Language | undefined>(
    languageState
  )
  const [socialMedia, setSocialMedia] = useRecoilState<
    ChartDataPoints[][] | undefined
  >(socialMediaState)
  const [timeAtHome, setTimeAtHome] = useRecoilState<
    ChartDataPoints[][] | undefined
  >(timeAtHomeState)

  // process vw_daily_features_trend
  useEffect(() => {
    const getTimeFromHour = (value?: number) => {
      if (!value) {
        return ""
      }
      const floor = Math.floor(value)
      const hour = 12 + floor
      const minutes = Math.floor(value - floor) * 60
      return `${hour > 12 ? hour - 12 : hour}:${
        minutes < 10 ? "0" : ""
      }${minutes}`
    }
    const getTimeUnitFromHour = (value?: number) => {
      if (!value) {
        return undefined
      }
      return value < 0 ? "pm" : "am"
    }
    const getPercent = (value?: number) => {
      if (!value) {
        return ""
      }
      return `${Math.floor(value * 100)}%`
    }
    const getFloor = (value?: string) => {
      if (!value) {
        return ""
      }
      return Math.floor(parseInt(value)).toString()
    }

    let newData = { ...initialTrends }
    client.vw_daily_features_trend.forEach((feature) => {
      switch (feature.key) {
        case Enum_Feature_Type_Enum.AmtWalkingDayMin:
          newData.walk = {
            label: "Walk",
            value: getFloor(feature.avg_this_week),
            unit: "min",
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.AmtExerciseDayMin:
          newData.exercise = {
            label: "Exercise",
            value: getFloor(feature.avg_this_week),
            unit: "min",
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.AmtStationaryDayMin:
          newData.stationary = {
            label: "Stationary",
            value: getFloor(feature.avg_this_week),
            unit: "min",
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.AmtAutomotiveDayMin:
          newData.driving = {
            label: "In Transit",
            value: getFloor(feature.avg_this_week),
            unit: "min",
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.ValBedtimeOffsetHour:
          newData.bedtime = {
            label: "Bedtime",
            value: getTimeFromHour(feature.avg_this_week),
            unit: getTimeUnitFromHour(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.ValWaketimeOffsetHour:
          newData.waketime = {
            label: "Waketime",
            value: getTimeFromHour(feature.avg_this_week),
            unit: getTimeUnitFromHour(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.AmtSleepDayMin:
          newData.timeInBed = {
            label: "Time in Bed",
            value: getFloor(feature.avg_this_week),
            unit: "min",
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.ValMorningness:
          newData.morningness = {
            label: "Morningness",
            value: getFloor(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.PropWordPosDay:
          newData.positiveWords = {
            label: "Positive Words",
            value: getPercent(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.PropWordNegDay:
          newData.negativeWords = {
            label: "Negative Words",
            value: getPercent(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.PropWordIDay:
          newData.selfFocusWords = {
            label: "Self-Focus Words",
            value: getPercent(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.PropWordAbsDay:
          // TODO is this the correct type?
          newData.rigidWords = {
            label: "Rigid Words",
            value: getPercent(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.NWordDay:
          newData.activeSocialMedia = {
            label: "Words Typed",
            value: getFloor(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
        case Enum_Feature_Type_Enum.PropGpsDayHome:
          newData.timeAtHome = {
            label: "Time at Home",
            value: getPercent(feature.avg_this_week),
            direction: feature.direction as Enum_Trend_Direction_Enum,
          }
          break
      }
    })
    setTrends(newData)
  }, [client.vw_daily_features_trend, setTrends])

  // process daily_features
  useEffect(() => {
    const newActivity: Activity = {
      walking: [],
      exercise: [],
      stationary: [],
      driving: [],
    }
    let walking: ChartDataPoints[] = []
    let exercise: ChartDataPoints[] = []
    let stationary: ChartDataPoints[] = []
    let driving: ChartDataPoints[] = []
    let previousWalkingDate: dayjs.Dayjs | undefined
    let previousExerciseDate: dayjs.Dayjs | undefined
    let previousStationaryDate: dayjs.Dayjs | undefined
    let previousDrivingDate: dayjs.Dayjs | undefined
    const newCategories: string[] = []

    const newMorningness: ChartDataPoints[][] = []
    let currentMorningness: ChartDataPoints[] = []
    let previousMorningnessDate: dayjs.Dayjs | undefined

    const newLanguage: Language = {
      positive: [],
      negative: [],
      selfFocus: [],
      rigid: [],
    }
    let positive: ChartDataPoints[] = []
    let negative: ChartDataPoints[] = []
    let selfFocus: ChartDataPoints[] = []
    let rigid: ChartDataPoints[] = []
    let previousPositiveDate: dayjs.Dayjs | undefined
    let previousNegativeDate: dayjs.Dayjs | undefined
    let previousSelfFocusDate: dayjs.Dayjs | undefined
    let previousRigidDate: dayjs.Dayjs | undefined

    const newSocialMedia: ChartDataPoints[][] = []
    let currentSocialMedia: ChartDataPoints[] = []
    let previousSocialMediaDate: dayjs.Dayjs | undefined

    const newTimeAtHome: ChartDataPoints[][] = []
    let currentTimeAtHome: ChartDataPoints[] = []
    let previousTimeAtHomeDate: dayjs.Dayjs | undefined

    for (let i = 0; i < 14; i++) {
      const day = dayjs().subtract(14 - i, "days")
      newCategories.push(`${day.format("ddd\nMM/DD")}`)
    }
    setCategories(newCategories)

    const sortedFeatures = client.daily_features
      .filter((feature) => {
        const featureDate = dayjs(feature.feature_date.split("T")[0])
        return (
          featureDate.isSameOrAfter(
            dayjs()
              .subtract(14, "days")
              .hour(0)
              .minute(0)
              .second(0)
              .millisecond(0)
          ) &&
          featureDate.isBefore(
            dayjs()
              .subtract(1, "day")
              .hour(12)
              .minute(0)
              .second(0)
              .millisecond(0)
          )
        )
      })
      .sort((a, b) =>
        a.feature_date < b.feature_date
          ? -1
          : a.feature_date > b.feature_date
          ? 1
          : 0
      )

    sortedFeatures.forEach((feature) => {
      const featureDate = dayjs(feature.feature_date.split("T")[0])
      switch (feature.key) {
        case Enum_Feature_Type_Enum.AmtWalkingDayMin:
          if (
            previousWalkingDate &&
            missingDay(featureDate, previousWalkingDate)
          ) {
            newActivity.walking.push(walking)
            walking = []
          }
          walking.push({
            x: `${featureDate.format("ddd\nMM/DD")}`,
            y: feature.value,
            label: "",
            unit: "min",
            name: "Walking",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.orange.dark,
            lightColor: colors.trendingData.orange.light,
          })
          previousWalkingDate = featureDate
          break
        case Enum_Feature_Type_Enum.AmtExerciseDayMin:
          if (
            previousExerciseDate &&
            missingDay(featureDate, previousExerciseDate)
          ) {
            newActivity.exercise.push(exercise)
            exercise = []
          }
          exercise.push({
            x: `${featureDate.format("ddd\nMM/DD")}`,
            y: feature.value,
            label: "",
            unit: "min",
            name: "Exercise",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.blue.dark,
            lightColor: colors.trendingData.blue.light,
          })
          previousExerciseDate = featureDate
          break
        case Enum_Feature_Type_Enum.AmtStationaryDayMin:
          if (
            previousStationaryDate &&
            missingDay(featureDate, previousStationaryDate)
          ) {
            newActivity.stationary.push(stationary)
            stationary = []
          }
          stationary.push({
            x: `${featureDate.format("ddd\nMM/DD")}`,
            y: feature.value,
            label: "",
            unit: "min",
            name: "Stationary",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.red.dark,
            lightColor: colors.trendingData.red.light,
          })
          previousStationaryDate = featureDate
          break
        case Enum_Feature_Type_Enum.AmtAutomotiveDayMin:
          if (
            previousDrivingDate &&
            missingDay(featureDate, previousDrivingDate)
          ) {
            newActivity.driving.push(driving)
            driving = []
          }
          driving.push({
            x: `${featureDate.format("ddd\nMM/DD")}`,
            y: feature.value,
            label: "",
            unit: "min",
            name: "In Transit",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.green.dark,
            lightColor: colors.trendingData.green.light,
          })
          previousDrivingDate = featureDate
          break
        case Enum_Feature_Type_Enum.ValMorningness:
          if (
            previousMorningnessDate &&
            missingDay(featureDate, previousMorningnessDate)
          ) {
            newMorningness.push(currentMorningness)
            currentMorningness = []
          }
          currentMorningness.push({
            x: featureDate.format("ddd\nMM/DD"),
            y: feature.value > 6 ? 6 : feature.value < -6 ? -6 : feature.value,
            label: "",
            unit: "",
            name: "Morningness",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.orange.dark,
            lightColor: colors.trendingData.orange.light,
            other: feature.value,
          })
          previousMorningnessDate = featureDate
          break
        case Enum_Feature_Type_Enum.PropWordPosDay:
          if (
            previousPositiveDate &&
            missingDay(featureDate, previousPositiveDate)
          ) {
            newLanguage.positive.push(positive)
            positive = []
          }
          positive.push({
            x: featureDate.format("ddd\nMM/DD"),
            y: feature.value > 0.25 ? 0.25 : feature.value,
            label: "",
            unit: "",
            name: "Positive Words",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.orange.dark,
            lightColor: colors.trendingData.orange.light,
            other: feature.value,
          })
          previousPositiveDate = featureDate
          break
        case Enum_Feature_Type_Enum.PropWordNegDay:
          if (
            previousNegativeDate &&
            missingDay(featureDate, previousNegativeDate)
          ) {
            newLanguage.negative.push(negative)
            negative = []
          }
          negative.push({
            x: featureDate.format("ddd\nMM/DD"),
            y: feature.value > 0.25 ? 0.25 : feature.value,
            label: "",
            unit: "",
            name: "Negative Words",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.blue.dark,
            lightColor: colors.trendingData.blue.light,
            other: feature.value,
          })
          previousNegativeDate = featureDate
          break
        case Enum_Feature_Type_Enum.PropWordIDay:
          if (
            previousSelfFocusDate &&
            missingDay(featureDate, previousSelfFocusDate)
          ) {
            newLanguage.selfFocus.push(selfFocus)
            selfFocus = []
          }
          selfFocus.push({
            x: featureDate.format("ddd\nMM/DD"),
            y: feature.value > 0.25 ? 0.25 : feature.value,
            label: "",
            unit: "",
            name: "Self-Focus Words",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.red.dark,
            lightColor: colors.trendingData.red.light,
            other: feature.value,
          })
          previousSelfFocusDate = featureDate
          break
        case Enum_Feature_Type_Enum.PropWordAbsDay:
          if (previousRigidDate && missingDay(featureDate, previousRigidDate)) {
            newLanguage.rigid.push(rigid)
            rigid = []
          }
          rigid.push({
            x: featureDate.format("ddd\nMM/DD"),
            y: feature.value > 0.25 ? 0.25 : feature.value,
            label: "",
            unit: "",
            name: "Rigid Words",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.green.dark,
            lightColor: colors.trendingData.green.light,
            other: feature.value,
          })
          previousRigidDate = featureDate
          break
        case Enum_Feature_Type_Enum.NWordDay:
          if (
            previousSocialMediaDate &&
            missingDay(featureDate, previousSocialMediaDate)
          ) {
            newSocialMedia.push(currentSocialMedia)
            currentSocialMedia = []
          }
          currentSocialMedia.push({
            x: featureDate.format("ddd\nMM/DD"),
            y: feature.value,
            label: "",
            unit: "",
            name: "Words Typed",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.orange.dark,
            lightColor: colors.trendingData.orange.light,
          })
          previousSocialMediaDate = featureDate
          break
        case Enum_Feature_Type_Enum.PropGpsDayHome:
          if (
            previousTimeAtHomeDate &&
            missingDay(featureDate, previousTimeAtHomeDate)
          ) {
            newTimeAtHome.push(currentTimeAtHome)
            currentTimeAtHome = []
          }
          currentTimeAtHome.push({
            x: featureDate.format("ddd\nMM/DD"),
            y: feature.value,
            label: "",
            unit: "",
            name: "Time at Home",
            date: featureDate.format("MMM DD, YYYY"),
            darkColor: colors.trendingData.orange.dark,
            lightColor: colors.trendingData.orange.light,
          })
          previousTimeAtHomeDate = featureDate
          break
      }
    })
    newActivity.walking.push(walking)
    newActivity.exercise.push(exercise)
    newActivity.stationary.push(stationary)
    newActivity.driving.push(driving)
    setActivity(newActivity)

    newMorningness.push(currentMorningness)
    setMorningness(newMorningness)

    newLanguage.positive.push(positive)
    newLanguage.negative.push(negative)
    newLanguage.selfFocus.push(selfFocus)
    newLanguage.rigid.push(rigid)
    setLanguage(newLanguage)

    newSocialMedia.push(currentSocialMedia)
    setSocialMedia(newSocialMedia)

    newTimeAtHome.push(currentTimeAtHome)
    setTimeAtHome(newTimeAtHome)
  }, [
    client.daily_features,
    setActivity,
    setCategories,
    setLanguage,
    setMorningness,
    setSocialMedia,
    setTimeAtHome,
  ])

  function transformTimes(bedtime: number, waketime: number) {
    bedtime = bedtime * -1
    waketime = waketime * -1
    const fourAm = -16

    if (bedtime < fourAm || waketime < fourAm) {
      bedtime = 0
      waketime = 0
    }

    return {
      bedtime,
      waketime,
    }
  }

  // process vw_daily_features_sleep
  useEffect(() => {
    const newSleep: Sleep[] = []
    for (let i = 0; i < 14; i++) {
      const currentDay = dayjs().subtract(14, "days").add(i, "days")
      const feature = client.vw_daily_features_sleep.find(
        (sleepDay) =>
          sleepDay.feature_date.split("T")[0] ===
          currentDay.format("YYYY-MM-DD")
      )
      if (feature) {
        const featureDate = dayjs(feature.feature_date.split("T")[0])
        const { bedtime, waketime } = transformTimes(
          feature.val_bedtime_offset_hour,
          feature.val_waketime_offset_hour
        )
        newSleep.push({
          x: `${featureDate.format("ddd\nMM/DD")}`,
          y: bedtime,
          y0: waketime,
          date: `${featureDate.format("MMM D, YYYY")}`,
          label: "",
          bedtime: feature.val_bedtime_offset_hour,
          waketime: feature.val_waketime_offset_hour,
        })
      } else {
        newSleep.push({
          x: `${currentDay.format("ddd\nMM/DD")}`,
          y: 0,
          y0: 0,
          date: `${currentDay.format("MMM D, YYYY")}`,
          label: "",
          bedtime: 0,
          waketime: 0,
        })
      }
    }
    setSleep(newSleep)
  }, [client.vw_daily_features_sleep, setSleep])

  return {
    trends,
    activity,
    categories,
    sleep,
    morningness,
    language,
    socialMedia,
    timeAtHome,
  }
}
